/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { useThemeObserver } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		position: 'relative',
		width: '100%',
		height: '100%',
		overflow: 'hidden',
	},
	background: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1,
	},
	content: {
		position: 'relative',
		zIndex: 100,
		width: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 0,
	},
});

interface ScreenLayoutProps {
	children: ReactNode;
	ModalBackgroundDark?: string;
	ModalBackgroundLight?: string;
	testId?: string;
}

interface BackgroundProps {
	children: ReactNode;
}

interface ContentProps {
	children: ReactNode;
}

const Background = ({ children }: BackgroundProps) => (
	<Box xcss={styles.background}>{children}</Box>
);

const Content = ({ children }: ContentProps) => <Box xcss={styles.content}>{children}</Box>;

export const ScreenLayout = ({
	children,
	testId,
	ModalBackgroundLight,
	ModalBackgroundDark,
}: ScreenLayoutProps) => {
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	return (
		<Box
			xcss={styles.container}
			testId={testId}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				backgroundImage:
					ModalBackgroundLight && ModalBackgroundDark
						? `url(${isDarkMode ? ModalBackgroundDark : ModalBackgroundLight})`
						: undefined,
			}}
		>
			{children}
		</Box>
	);
};

ScreenLayout.Background = Background;
ScreenLayout.Content = Content;
